import WorkSans from "WorkSans-VariableFont_wght.ttf";
import MontserratRegular from "Montserrat-Regular.ttf";
import SegoeUIRegular from "segoe-ui.ttf";
import SegoeUISemiBold from "segoe-ui-semibold.ttf";
import SegoeUIBold from "segoe-ui-bold.ttf";
import SegoeUIBlack from "segoe-ui-black.ttf";
import createBreakpoints from "@mui/system/createTheme/createBreakpoints";
import { variants as buttonVariants } from "./mui_components/button";

const breakpoints = createBreakpoints({});
const primaryFontFamily =
  "'Segoe UI Regular', 'Work Sans', Roboto, Helvetica, Arial, sans-serif";
const headingFontFamily =
  "'Segoe UI Bold', 'Segoe UI Semibold', 'Work Sans Bold', 'Work Sans', Roboto, Helvetica, Arial, sans-serif";

const theme = {
  typography: {
    fontFamily: primaryFontFamily,
    emphasis: {
      wordBreak: "normal",
      fontFamily: primaryFontFamily,
      fontSize: "64px",
      fontWeight: 600,
      lineHeight: "75px",
      [breakpoints.down("lg")]: {
        fontSize: "48px",
      },
    },
    h1: {
      wordBreak: "normal",
      fontFamily: primaryFontFamily,
      fontSize: "36px",
      fontWeight: 400,
      [breakpoints.down("lg")]: {
        fontSize: "18px",
      },
    },
    h2: {
      wordBreak: "normal",
      fontFamily: primaryFontFamily,
      fontSize: "28px",
      fontWeight: 400,
      letterSpacing: "-0.3333333432674408px",
      color: "#222222",
      [breakpoints.down("lg")]: {
        fontSize: "16px",
      },
    },
    h3: {
      wordBreak: "normal",
      fontFamily: primaryFontFamily,
      fontSize: "24px",
      fontWeight: 400,
      [breakpoints.down("lg")]: {
        fontSize: "14px",
      },
    },
    h4: {
      wordBreak: "normal",
      fontFamily: primaryFontFamily,
      fontSize: "22px",
      fontWeight: 400,
      letterSpacing: "-0.33px",
      lineHeight: 1.195,
      [breakpoints.down("lg")]: {
        fontSize: "12px",
      },
    },
    h5: {
      wordBreak: "normal",
      fontFamily: headingFontFamily,
      fontSize: "18px",
      fontWeight: 400,
      letterSpacing: "-0.33px",
      [breakpoints.down("lg")]: {
        fontSize: "10px",
      },
    },
    h6: {
      wordBreak: "normal",
      fontFamily: headingFontFamily,
      fontSize: "16px",
      fontWeight: 400,
      letterSpacing: "-0.33px",
      [breakpoints.down("lg")]: {
        fontSize: "10px",
      },
    },
    h7: {
      wordBreak: "normal",
      fontFamily: headingFontFamily,
      fontSize: "14px",
      fontWeight: 400,
      letterSpacing: "-0.33px",
      [breakpoints.down("lg")]: {
        fontSize: "10px",
      },
    },
    h8: {
      wordBreak: "normal",
      fontFamily: headingFontFamily,
      fontSize: "12px",
      fontWeight: 400,
      letterSpacing: "-0.33px",
      [breakpoints.down("lg")]: {
        fontSize: "10px",
      },
    },
    body1: {
      wordBreak: "normal",
      fontFamily: primaryFontFamily,
      fontStyle: "normal",
      fontSize: "16px",
      fontWeight: 300,
      letterSpacing: "-0.3333333432674408px",
      [breakpoints.down("lg")]: {
        fontSize: "14px",
      },
    },
    body1bold: {
      wordBreak: "normal",
      fontFamily: primaryFontFamily,
      fontStyle: "normal",
      fontSize: "16px",
      fontWeight: 700,
      letterSpacing: "-0.3333333432674408px",
      [breakpoints.down("lg")]: {
        fontSize: "14px",
      },
    },
    body2: {
      wordBreak: "normal",
      fontFamily: primaryFontFamily,
      fontSize: "14px",
      fontStyle: "normal",
      fontWeight: 400,
      letterSpacing: "0.15000000596046448px",
      [breakpoints.down("lg")]: {
        fontSize: "12px",
      },
    },
    body2bold: {
      wordBreak: "normal",
      fontFamily: primaryFontFamily,
      fontSize: "14px",
      fontStyle: "normal",
      fontWeight: 600,
      letterSpacing: "0.15000000596046448px",
      [breakpoints.down("lg")]: {
        fontSize: "12px",
      },
    },
    body3: {
      wordBreak: "normal",
      fontFamily: primaryFontFamily,
      fontSize: "12px",
      fontStyle: "normal",
      fontWeight: 400,
      letterSpacing: "0.05000000596046448px",
    },
    body3bold: {
      wordBreak: "normal",
      fontFamily: primaryFontFamily,
      fontSize: "12px",
      fontStyle: "normal",
      fontWeight: 600,
      letterSpacing: "0.05000000596046448px",
    },
    body4: {
      wordBreak: "normal",
      fontFamily: primaryFontFamily,
      fontSize: "10px",
      fontStyle: "normal",
      fontWeight: 400,
      letterSpacing: "0.15000000596046448px",
    },
    subtitle0: {
      wordBreak: "normal",
      fontFamily: primaryFontFamily,
      fontSize: "48px",
      fontWeight: 400,
      lineHeight: "56px",
    },
    subtitle1: {
      wordBreak: "normal",
      fontFamily: primaryFontFamily,
      fontSize: "18px",
      fontWeight: 500,
      lineHeight: 1.2,
      letterSpacing: "0.15000000596046448px",
    },
    subtitle2: {
      wordBreak: "normal",
      fontFamily: primaryFontFamily,
      fontSize: "16px",
      fontWeight: 300,
      letterSpacing: "-0.33px",
      color: "#11111180",
    },
    caption: {
      display: "block",
      wordBreak: "normal",
      fontFamily: primaryFontFamily,
      fontSize: "11px",
      fontWeight: 400,
      letterSpacing: "0px",
      lineHeight: "14.08px",
      color: "#00000080",
    },
  },
  palette: {
    common: {
      black: "#000",
      white: "#fff",
      blue: "#2A334E",
      sh_lightgrey: "rgba(0, 0, 0, 0.06)",
      grey_background: "rgba(255, 255, 255, 0.15)",
      sh_grey: "#B2B2B2",
      main: "#023331",
      blue_grey: "#D3E0EA",
    },
    background: { paper: "#fff", default: "#F9F9F9", neutral: "#D7D7D7" },
    warning: { main: "#FAC403" },
    primary: {
      lighter: "rgba(2, 51, 49, 0.12)",
      light: "#166D4C",
      main: "#023331",
      dark: "#011a18",
      smoke: "rgba(2, 51, 49, 0.06)",
      contrastText: "#fff",
      green: "#166D4C",
    },
    secondary: {
      lighter: "rgba(22, 109, 76, 0.12)",
      light: "#CEE1DA",
      main: "#166D4C",
      dark: "#0f4a33",
      smoke: "#CEE1DA",
      contrastText: "#fff",
      grey: "#F1F1F1",
      grey_strong: "#E8E8E8",
      black: "#1B1B1B",
      box: "#CEE1DA",
      hover: "#D1F812",
    },
    error: {
      smoke: "rgba(248, 128, 120, 0.1)",
      light: "rgba(248, 128, 120, 1)",
      main: "#f44336",
      dark: "rgba(227, 27, 12, 1)",
      contrastText: "#fff",
    },
    text: {
      main: "#023331",
      blue: "#222222",
      light_blue: "#94A3D0",
      light_green: "#CEE1DA",
      white: "rgba(255, 255, 255, 0.75)",
      dark: "rgba(0, 0, 0)",
      primary: "rgba(0, 0, 0, 0.75)",
      placeholder: "rgba(0, 0, 0, 0.625)",
      green: "#166D4C",
      secondary: "rgba(0, 0, 0, 0.5)",
      disabled: "rgba(0, 0, 0, 0.38)",
      hint: "rgba(0, 0, 0, 0.38)",
      warning: "#E4A11B",
      light_grey: "#828282",
      strong: "#111111"
    },
    linkedin: {
      primary: "#0e76a8",
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @font-face {
          font-family: 'Segoe UI Regular';
          font-style: normal;
          font-weight: 400;
          src: local('Segoe UI'), url(${SegoeUIRegular}) format('truetype');
        }
        @font-face {
          font-family: 'Segoe UI Semibold';
          font-style: normal;
          font-weight: 600;
          src: local('Segoe UI Semibold'), local('Segoe UI'), url(${SegoeUISemiBold}) format('truetype');
        }
        @font-face {
          font-family: 'Segoe UI Bold';
          font-style: normal;
          font-weight: 700;
          src: local('Segoe UI Bold'), url(${SegoeUIBold}) format('truetype');
        }
        @font-face {
          font-family: 'Segoe UI Black';
          font-style: normal;
          font-weight: 900;
          src: local('Segoe UI Black'), local('Segoe UI'), url(${SegoeUIBlack}) format('truetype');
        }
        @font-face {
          font-family: 'Work Sans';
          font-style: normal;
          font-weight: 300;
          src: local('WorkSans'), url(${WorkSans}) format('truetype');
        }
        @font-face {
          font-family: 'Work Sans Bold';
          font-style: normal;
          src: local('WorkSans'), url(${WorkSans}) format('truetype');
        }
        @font-face {
          font-family: 'Montserrat';
          font-style: normal;
          src: local('Montserrat'), local('Montserrat-Regular'), url(${MontserratRegular}) format('truetype');
        }
      `,
    },
    MuiButton: {
      variants: buttonVariants,
    },
  },
};
export default theme;
